import { apiFetch } from "./config.js";
/**
 * `issue_tag` has no repeated-param support on the backend (unlike
 * `status`), so an array here still needs to collapse to one value - this
 * only comma-joins, it never sends more than one real query occurrence.
 */
function joinParam(value) {
    if (value === undefined)
        return undefined;
    return Array.isArray(value) ? value.join(",") : value;
}
/**
 * List claim candidates, optionally filtered by status and/or issue tag.
 * Plain async function - wrap in `useQuery` with a queryKey of
 * `["claim-candidates", params]`.
 */
export async function listClaimCandidates(params = {}, signal) {
    return apiFetch("/claim-candidates", {
        method: "GET",
        signal,
        query: {
            // Repeated `?status=a&status=b`, matching the backend's `list[...]`
            // query param - never comma-joined (see joinParam's own docstring
            // for why that would be wrong here specifically).
            status: params.status === undefined ? undefined : Array.isArray(params.status) ? params.status : [params.status],
            issue_tag: joinParam(params.issue_tag),
            recommended_forum: params.recommended_forum,
            search: params.search,
            ledger_ready_only: params.ledger_ready_only ? "true" : undefined,
            limit: params.limit,
            offset: params.offset,
        },
    });
}
/**
 * Get one claim candidate with its full precedent chain resolved to
 * Findings + their current AuthorityStatus. Wrap in `useQuery` with a
 * queryKey of `["claim-candidates", id]`.
 */
export async function getClaimCandidate(id, signal) {
    return apiFetch(`/claim-candidates/${encodeURIComponent(id)}`, {
        method: "GET",
        signal,
    });
}
/**
 * Accept, reject, or defer a claim candidate. `rejection_reason` should be
 * supplied when moving to "rejected" (or "deferred"). Wrap in
 * `useMutation`, then invalidate `["claim-candidates"]` (list) and
 * `["claim-candidates", id]` (detail) on success.
 *
 * The backend's transition endpoint is `POST .../transition` with a
 * `{"status": ..., "reason": ...}` body (see `workbench_api`'s
 * `ClaimTransitionRequest` / `workbench_api.transitions`) rather than a
 * `PATCH .../status` with `rejection_reason` - this function keeps this
 * client's own request shape (named after the field it actually sets,
 * `ClaimCandidate.rejection_reason`) and translates it to the wire shape
 * the service expects.
 */
export async function updateClaimCandidateStatus(id, request) {
    return apiFetch(`/claim-candidates/${encodeURIComponent(id)}/transition`, {
        method: "POST",
        body: { status: request.status, reason: request.rejection_reason ?? null },
    });
}
/** Saves a reviewer's edits to the draft argument. `draft_argument_original` is never touched by this. */
export async function updateDraftArgument(id, draftArgument) {
    return apiFetch(`/claim-candidates/${encodeURIComponent(id)}/draft-argument`, {
        method: "PATCH",
        body: { draft_argument: draftArgument },
    });
}
/** Discards a reviewer's edits, restoring `draft_argument` to `draft_argument_original`. */
export async function revertDraftArgument(id) {
    return apiFetch(`/claim-candidates/${encodeURIComponent(id)}/draft-argument/revert`, {
        method: "POST",
    });
}
/** Mark selected in-progress tranches as counsel-validated. */
export async function validateClaimTranches(claimIds) {
    return apiFetch("/claim-candidates/validate", {
        method: "POST",
        body: { claim_ids: claimIds },
    });
}
